import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sezioni centrali riordinabili/nascondibili (Navbar, Hero e Footer restano fissi)
export const DEFAULT_SECTIONS = [
  { id: "madebyadmins", label: "Da amministratori" },
  { id: "howitworks", label: "Come funziona" },
  { id: "scopeclarity", label: "Ambito" },
  { id: "features", label: "Funzioni AI" },
  { id: "video", label: "Video demo" },
  { id: "savings", label: "Risparmio" },
  { id: "cashback", label: "Programma Partner" },
  { id: "benefits", label: "Perche Sedio" },
  { id: "testimonials", label: "Testimonianze" },
  { id: "freetrial", label: "Prova gratuita" },
  { id: "midcta", label: "CTA intermedia" },
  { id: "leadform", label: "Contatti" },
  { id: "faq", label: "FAQ" },
];

const ContentContext = createContext(null);
export const useContent = () => useContext(ContentContext);

const LS_TOKEN = "sedio_admin_token";

function mergeSections(saved) {
  const savedArr = Array.isArray(saved) ? saved : [];
  const byId = new Map(savedArr.map((s) => [s.id, s]));
  const ordered = [];
  savedArr.forEach((s) => {
    const def = DEFAULT_SECTIONS.find((d) => d.id === s.id);
    if (def) ordered.push({ id: s.id, label: def.label, visible: s.visible !== false });
  });
  DEFAULT_SECTIONS.forEach((d) => {
    if (!byId.has(d.id)) ordered.push({ id: d.id, label: d.label, visible: true });
  });
  return ordered.length ? ordered : DEFAULT_SECTIONS.map((d) => ({ ...d, visible: true }));
}

export function ContentProvider({ children }) {
  const [texts, setTexts] = useState({});
  const [theme, setTheme] = useState({});
  const [sections, setSections] = useState(DEFAULT_SECTIONS.map((d) => ({ ...d, visible: true })));
  const [loaded, setLoaded] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const editMode = window.location.pathname.replace(/\/$/, "") === "/login" || params.get("edit") === "1";
  const authed = editMode && !!token;

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/content`)
      .then((res) => {
        if (!active) return;
        const d = res.data || {};
        setTexts(d.texts || {});
        setTheme(d.theme || {});
        setSections(mergeSections(d.sections));
      })
      .catch(() => {})
      .finally(() => active && setLoaded(true));
    return () => {
      active = false;
    };
  }, []);

  const getText = useCallback((path, fallback) => (texts[path] !== undefined && texts[path] !== null ? texts[path] : fallback), [texts]);

  // Gestisce il click di una CTA: link salvato (path) oppure target di default.
  // Supporta: URL esterni (http/https), mailto:, tel: e id di sezione (#id o id).
  const ctaClick = useCallback((path, defaultTarget) => (e) => {
    const raw = texts[path] !== undefined && texts[path] !== null && texts[path] !== "" ? texts[path] : defaultTarget;
    const t = String(raw || "").trim();
    if (/^https?:\/\//i.test(t)) {
      if (e && e.preventDefault) e.preventDefault();
      window.open(t, "_blank", "noopener,noreferrer");
    } else if (/^(mailto:|tel:)/i.test(t)) {
      window.location.href = t;
    } else {
      scrollToId(t.replace(/^#/, ""));
    }
  }, [texts]);

  const setText = useCallback((path, value) => {
    setTexts((prev) => {
      if (prev[path] === value) return prev;
      return { ...prev, [path]: value };
    });
    setDirty(true);
  }, []);

  const setThemeVar = useCallback((key, value) => {
    setTheme((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const toggleSection = useCallback((id) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)));
    setDirty(true);
  }, []);

  const moveSection = useCallback((id, dir) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(target, 0, item);
      return next;
    });
    setDirty(true);
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await axios.post(`${API}/admin/login`, { username, password });
    const tk = res.data.token;
    localStorage.setItem(LS_TOKEN, tk);
    setToken(tk);
    return tk;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(LS_TOKEN);
    setToken("");
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        texts,
        theme,
        sections: sections.map((s) => ({ id: s.id, visible: s.visible !== false })),
      };
      await axios.put(`${API}/content`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setDirty(false);
      return true;
    } finally {
      setSaving(false);
    }
  }, [texts, theme, sections, token]);

  const value = {
    texts, theme, sections, loaded, editMode, authed, token, dirty, saving,
    getText, setText, ctaClick, setThemeVar, toggleSection, moveSection, login, logout, save,
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}
